import React from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { Check } from 'lucide-react';
import BookingWidget from '../components/BookingWidget';
import { useTranslation } from 'react-i18next';

const STEP_KEYS = ['step1', 'step2', 'step3'];
const TRUST_FEATURE_KEYS = ['secure', 'confirmation', 'cancellation'];

const ReserverPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#f8f8f8]" data-testid="reserver-page">
          <Seo {...seo.reserver} />
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-white border-b border-neutral-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left Column - Info */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block px-4 py-2 bg-black text-white rounded-full text-xs uppercase tracking-[0.2em] font-medium mb-6">
                {t('reserverPage.badge')}
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-black tracking-tight mb-6">
                {t('reserverPage.title1')}<br />
                <span className="text-neutral-400">{t('reserverPage.title2')}</span>
              </h1>
              <p className="text-lg md:text-xl text-neutral-600 mb-10 max-w-lg leading-relaxed">
                {t('reserverPage.description')}
              </p>

              {/* Steps */}
              <div className="space-y-5 mb-10">
                {STEP_KEYS.map((stepKey, i) => (
                  <div key={stepKey} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-black font-semibold mb-1">
                        {t(`reserverPage.steps.${stepKey}.title`)}
                      </h3>
                      <p className="text-neutral-500 text-sm">
                        {t(`reserverPage.steps.${stepKey}.desc`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Fixed fares card */}
              <div className="p-6 bg-neutral-50 border border-neutral-100 rounded-2xl">
                <h3 className="text-black font-heading font-semibold mb-4">
                  {t('reserverPage.fixedFares.title')}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-3 border-b border-neutral-200">
                    <span className="text-neutral-600">{t('reserverPage.fixedFares.fare1Route')}</span>
                    <span className="text-black font-bold text-lg">{t('reserverPage.fixedFares.fare1Price')}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-neutral-600">{t('reserverPage.fixedFares.fare2Route')}</span>
                    <span className="text-black font-bold text-lg">{t('reserverPage.fixedFares.fare2Price')}</span>
                  </div>
                </div>
              </div>

              {/* KG-3R (Truth Before Reach): fabricated 4.9/5 star rating removed — no real reviews. */}
            </motion.div>

            {/* Right Column - Booking Widget */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:sticky lg:top-32"
            >
              <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] p-1 border border-neutral-100">
                <BookingWidget />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Features */}
      <section className="py-16 bg-[#f8f8f8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-6">
            {TRUST_FEATURE_KEYS.map((featKey, i) => (
              <motion.div
                key={featKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4 p-6 bg-white border border-neutral-100 rounded-xl"
              >
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-1">
                    {t(`reserverPage.trustFeatures.${featKey}.title`)}
                  </h3>
                  <p className="text-neutral-500 text-sm">
                    {t(`reserverPage.trustFeatures.${featKey}.desc`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReserverPage;
